import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är skillnaden mellan videoproduktion och filmproduktion?",
    a: "Termerna används ofta synonymt. Historiskt syftade videoproduktion på snabbare webbfilm och filmproduktion på TV/biograf. 2026 producerar samma team för båda kanalerna — vi kallar det filmproduktion när strategi och regi är centralt, video när det är snabb content-leverans.",
  },
  {
    q: "Vad ingår i ett videoproduktionsuppdrag?",
    a: "Manus, planering, inspelning, klipp, grade, ljud, undertexter, miniatyrer och formatversionering. Hos Fenny ingår strategi och målmätning i alla uppdrag — vi börjar med vad ni vill att tittaren ska göra eller känna, inte med kameran.",
  },
  {
    q: "Hur snabbt kan ni leverera en video?",
    a: "Pulse-kunder har första klippet ute inom 10–14 dagar. Standard videoproduktion: 2–4 veckor från brief till leverans. Snabba sociala-only-cuts kan levereras inom 5 arbetsdagar för befintliga Pulse-kunder.",
  },
  {
    q: "Producerar ni för LinkedIn, Instagram, TikTok och YouTube?",
    a: "Ja, och vi versionerar för alla i samma produktion. En inspelning blir typiskt 4–6 färdiga klipp: 16:9 för YouTube, 9:16 för Reels/TikTok/Shorts, 1:1 för LinkedIn feed, 4:5 för Meta feed. Plus undertexter och hooks-cut.",
  },
  {
    q: "Behöver vi en filmstudio eller kan ni filma hos oss?",
    a: "Vi filmar där det fungerar bäst. På er arbetsplats, på location, eller i vår studio. Många produktioner blir starkare när vi filmar i en verklig miljö — det syns på filmen.",
  },
  {
    q: "Vad kostar videoproduktion?",
    a: "Enklare videoproduktion landar typiskt på 25–80 kkr per projekt. Större produktioner med flera spelningsdagar 80–300 kkr. Pulse-abonnemang ger lägre kostnad per film vid löpande output.",
  },
];

export const Route = createFileRoute("/videoproduktion")({
  head: () => ({
    meta: [
      { title: "Videoproduktion för företag — pris, process och val av byrå" },
      {
        name: "description",
        content:
          "Videoproduktion från Fenny — manus, regi, inspelning, klipp, grade och versionering för LinkedIn, Instagram, TikTok, YouTube och sajt. Första klippet inom 10–14 dagar.",
      },
      { property: "og:title", content: "Videoproduktion — Fenny" },
      { property: "og:description", content: "Strategisk videoproduktion för alla sociala kanaler. Versionerad och redo att publicera." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/videoproduktion" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/videoproduktion" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/videoproduktion", name: "Videoproduktion för företag — pris, process och val av byrå", description: "Videoproduktion från Fenny — manus, regi, inspelning, klipp, grade och versionering för LinkedIn, Instagram, TikTok, YouTube och sajt. Första klippet inom 10–14 dagar."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Videoproduktion",
            path: "/videoproduktion",
            serviceType: "Videoproduktion",
            description:
              "Strategisk videoproduktion för LinkedIn, Instagram, TikTok, YouTube och sajt. Manus, inspelning, klipp, grade, ljud och formatversionering.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Videoproduktion", path: "/videoproduktion" },
          ]),
        ),
      },
    ],
  }),
  component: VideoproduktionPage,
});

function VideoproduktionPage() {
  return (
    <LandingPage
      quickAnswerId="sv_vad_kostar_videoproduktion"
      eyebrow="Videoproduktion"
      title={<>Videoproduktion som tar er över algoritmens tröskel.</>}
      lede="Vi gör videoproduktion för marknadsteam som behöver kontinuerligt rörligt innehåll — inte ett tjusigt projekt varannan höst. Manus, regi, inspelning, klipp och versionering för alla relevanta kanaler i samma pass."
      tldr={<p>Videoproduktion omfattar hela kedjan från manus och inspelning till klipp, ljud, grade och versionering för alla format (9:16, 1:1, 16:9). Fenny levererar videoproduktion via <strong>Pulse-abonnemang från 14 995 kr/mån</strong> (2–8 klipp/mån) eller <strong>Prime-projekt</strong> till fastpris för enskilda kampanjer.</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="videoproduktion"
      bullets={[
        { title: "Alla format i ett pass", body: "16:9, 9:16, 1:1, 4:5 — plus undertexter och hooks-cut. Ni publicerar samma dag som filmen levereras." },
        { title: "10–14 dagar till första klipp", body: "Pulse-kunder har sin första film ute inom 10–14 dagar. Inga månader av onboarding." },
        { title: "Mätbart från start", body: "Vi sätter mål per produktion: view-through, watch-time, CTR. Bygger filmen mot det och rapporterar." },
      ]}
      pricing={{
        heading: "Vad kostar videoproduktion 2026?",
        intro: "Riktpriser exklusive moms för produktion i Sverige.",
        rows: [
          { format: "Kortformat / social", scope: "1 dag, flera klipp, versionering", price: "25 000–80 000 kr", lead: "2–3 veckor" },
          { format: "Företagsfilm", scope: "Manus, 1–2 dagar, klipp och grade", price: "80 000–250 000 kr", lead: "3–5 veckor" },
          { format: "Kampanj / reklamfilm", scope: "Koncept, casting, regi, post", price: "150 000–300 000 kr", lead: "4–6 veckor" },
          { format: "Pulse-abonnemang", scope: "2–8 klipp per månad, löpande", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
        footnote: "Löpande behov blir billigare per film via abonnemang än som separata projekt.",
      }}
      process={{
        heading: "Videoproduktion steg för steg",
        steps: [
          { label: "Brief och insights", body: "Mål, målgrupp och kanal — vi sätter mätpunkt innan manus.", duration: "vecka 1" },
          { label: "Manus", body: "Manus och hook-varianter för skriftligt godkännande.", duration: "vecka 1" },
          { label: "Pre-production", body: "Location, medverkande, teknik och schema.", duration: "vecka 2" },
          { label: "Inspelning", body: "Typiskt 1 dag med eget team.", duration: "vecka 3" },
          { label: "Post och versionering", body: "Klipp, grade, ljud, undertexter och format per kanal.", duration: "vecka 4" },
        ],
      }}
      relatedLinksFor="/videoproduktion"
      sections={[
        {
          id: "vad-ar-videoproduktion",
          heading: "Vad är videoproduktion?",
          body: (
            <>
              <p>Videoproduktion är att ta fram rörligt innehåll från idé till färdig film: planering och manus, inspelning och sedan efterproduktion med klipp, färg, ljud och format. Det är den del av <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/fragor/vad-ar-mediaproduktion">mediaproduktion</a> som handlar om rörlig bild.</p>
              <p>Den här sidan är för er som ska köpa videoproduktion till ett företag, inte för er som vill lära er filma. Här ser ni vad som ingår, vad det kostar och hur ni väljer rätt leverantör.</p>
            </>
          ),
        },
        {
          id: "valja-leverantor",
          heading: "Så väljer ni leverantör för videoproduktion",
          body: (
            <ul>
              <li>Be om fast pris där inspelningsdagar, antal versioner och revideringsrundor står med.</li>
              <li>Kontrollera att <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/fragor/vad-ar-post-production">efterproduktionen</a> görs i eget team, så kvaliteten blir jämn mellan filmerna.</li>
              <li>Fråga hur filmen versioneras för varje kanal — en inspelning bör ge flera färdiga klipp.</li>
              <li>Jämför ett <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmbolag">filmbolag</a> mot en filmbyrå: behöver ni bara produktion, eller någon som även äger strategin?</li>
            </ul>
          ),
        },
        {
          id: "vad-vi-producerar",
          heading: "Vilka typer av video gör vi?",
          body: (
            <>
              <p>
                Brand-video och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a> för paid social. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/sociala-medier-film">Sociala medier-film</a> för Reels, TikTok och Shorts. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">Företagsfilm</a> som faktiskt blir sedd. Kundcase och referensfilm. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">Produktfilm</a> för e-handel och B2B. Utbildningsfilm och webinar-inspelning. Employer branding och rekrytering.
              </p>
              <p>
                Den gemensamma nämnaren är att vi börjar med distributionen — vilken kanal, vilken målgrupp, vilket beteende — och bygger filmen mot det.
              </p>
            </>
          ),
        },
        {
          id: "process",
          heading: "Så går en videoproduktion till",
          body: (
            <>
              <p>
                <strong>Vecka 1:</strong> brief, insights och manus. <strong>Vecka 2:</strong> pre-production, planering och logistik. <strong>Vecka 3:</strong> inspelning (typiskt 1 dag). <strong>Vecka 4:</strong> klipp, grade, ljud och versionering. Leverans i delad mapp.
              </p>
              <p>
                Pulse-abonnemang komprimerar detta: vi spelar in i block 1–2 gånger per månad och levererar löpande färdiga filmer i takt med er publiceringsplan.
              </p>
            </>
          ),
        },
        {
          id: "geografi",
          heading: "Var producerar vi?",
          body: (
            <p>
              Sverige-täckande, med lokala team i <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/stockholm">Stockholm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">Göteborg</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">Malmö</a>. Resa inom Sverige är inkluderat i Pulse-abonnemang.
            </p>
          ),
        },
        {
          id: "pris",
          heading: "Vad kostar videoproduktion?",
          body: (
            <p>
              Enklare videoproduktion landar typiskt på 25–80 kkr. Större produktioner 80–300 kkr. Pulse-abonnemang sänker kostnaden per film vid löpande output. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna fram er nivå →</a>
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      closing={{
        title: "Behöver ni videoproduktion i takt med marknaden?",
        body: "Boka 30 minuter — vi pekar ut vilka format som skulle ge er mest effekt nästa kvartal.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Räkna pris", to: "/priser" },
      }}
    />
  );
}
